// =============================================================================
// src/lib/identity.ts  –  Persistent device identity (localStorage)
// =============================================================================

const ADJECTIVES = ['Happy', 'Clever', 'Brave', 'Swift', 'Bright', 'Calm', 'Eager', 'Jolly'];
const ANIMALS    = ['Dolphin', 'Fox', 'Lion', 'Eagle', 'Panda', 'Tiger', 'Wolf', 'Bear'];
const DEVICES    = ['smartphone', 'laptop', 'desktop'] as const;

export type DeviceType = typeof DEVICES[number];

export interface Identity {
  peerId:    string;
  nickname:  string;
  device:    DeviceType;
  publicKey: string;          // base64 ECDH — stub until TURN/key exchange needed
  avatar:    string;
}

const STORAGE_KEY = 'wavedrop_identity';

function randomChoice<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}

function nanoid(len = 16): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from(crypto.getRandomValues(new Uint8Array(len)), b => chars[b % chars.length]).join('');
}

function detectDevice(): DeviceType {
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|android|mobile/.test(ua)) return 'smartphone';
  if (/ipad|tablet/.test(ua)) return 'laptop';
  return 'desktop';
}

export function getOrCreateIdentity(): Identity {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw) {
    try { return JSON.parse(raw) as Identity; } catch { /* fallthrough */ }
  }

  const adj      = randomChoice(ADJECTIVES);
  const animal   = randomChoice(ANIMALS);
  const nickname = `${adj} ${animal}`;
  const identity: Identity = {
    peerId:    nanoid(16),
    nickname,
    device:    detectDevice(),
    publicKey: '',           // filled lazily if needed
    avatar:    `https://api.dicebear.com/7.x/bottts/svg?seed=${encodeURIComponent(nickname)}&backgroundColor=e2e8f0`,
  };

  localStorage.setItem(STORAGE_KEY, JSON.stringify(identity));
  return identity;
}

// LAN room persisted per identity so the same peer rejoins the same LAN room
const LAN_ROOM_KEY = 'wavedrop_lan_room';

export function getLanRoomId(): string | null {
  return localStorage.getItem(LAN_ROOM_KEY);
}

export function setLanRoomId(roomId: string): void {
  localStorage.setItem(LAN_ROOM_KEY, roomId);
}

export function clearLanRoomId(): void {
  localStorage.removeItem(LAN_ROOM_KEY);
}
